/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { shouldEnableContextMenu, isContextMenuVisible } from "selectors/contextMenuSelectors";
import { isContextMenuTriggeredFromTouch } from "utils/EventHandlingUtils";
import { getPageZoomLevel } from "selectors/zoom";
import { pathExistsInPathName } from "utils/DocumentUtils";
import { sortAndGetMenuItems } from "utils/ContextMenuUtils";
import * as constants from "constants/constants";
import { getShouldUseModernViewer } from "selectors/appSelectors";
import { isUndefined, forEach, flatten, isFunction } from "utils/lodashUtils";
import { contextMenuTriggerType } from "dc-preview-utils/src/Constants/enums";
import { SignalDispatcherAsync } from "dc-preview-utils/src/Utils/SignalDispatcher";
import { getShouldEnableEncryptedWrite } from "selectors/overlayDropinSelectors";
import { isFilePasswordProtected } from "../selectors/documentSelectors";
import { trackContextMenuShown } from "analytics/events";

class ContextMenuManager {
  constructor() {
    this.reset();
  }

  reset() {
    this.signalDispatcherMap = {};
  }

  isString(str) {
    return str && typeof str.valueOf() === "string";
  }

  getSignalDispatcherByType(type) {
    return this.signalDispatcherMap[type];
  }

  unregister(type, observer) {
    if (this.signalDispatcherMap[type]) {
      const signalDispatcher = this.getSignalDispatcherByType(type);
      signalDispatcher.unregister(type, observer);
    }
  }

  // TODO: name parameter is not in use; it can be removed
  subscribe({ name, type, contextMenuHandler }) {
    if (this.isString(name) && contextMenuHandler instanceof Function) {
      if (this.signalDispatcherMap[type] === undefined) {
        this.signalDispatcherMap[type] = new SignalDispatcherAsync();
      }
      const signalDispatcher = this.getSignalDispatcherByType(type);
      signalDispatcher.on(type, contextMenuHandler);
    }
  }

  // using default value of extraParams as empty object because when we right click on grey area of document, extraParams comes as undefined and hence there is an error in console when we access extraParams.contextMenuStyle
  triggerContextMenu(eventObj, stores, extraParams = {}, type = contextMenuTriggerType.RIGHT_CLICK) {
    if (
      !getShouldEnableEncryptedWrite(stores) &&
      isFilePasswordProtected(stores) &&
      type !== contextMenuTriggerType.HUD_OVERFLOW_BUTTON_CLICK
    ) {
      return;
    }
    if (isContextMenuTriggeredFromTouch(eventObj)) {
      eventObj.stopPropagation();
      eventObj.preventDefault();
      return;
    }
    if (shouldEnableContextMenu(stores, eventObj.target)) {
      if (eventObj.stopPropagation) {
        eventObj.stopPropagation();
        eventObj.preventDefault();
      }

      if (type !== contextMenuTriggerType.TEXT_SELECTION || !isContextMenuVisible(stores)) {
        trackContextMenuShown(type);
      }

      this.clearMenuItems(stores);
      stores.contextMenuStore.setContextMenuTriggerEventObj(eventObj);
      stores.contextMenuStore.setPosition(eventObj.clientX, eventObj.clientY);
      stores.contextMenuStore.setDefaultPlacement(eventObj.placement);
      stores.contextMenuStore.setTriggerType(type);
      stores.contextMenuStore.setTriggerBox(eventObj.bbox);
      stores.contextMenuStore.setTriggerPage(!isNaN(eventObj.pageIndex) ? eventObj.pageIndex : extraParams.pageIndex);
      stores.contextMenuStore.setPageZoomLevel(getPageZoomLevel(stores, eventObj.pageIndex));
      stores.contextMenuStore.setIsContextMenu(extraParams.isContextMenu);
      stores.contextMenuStore.setTrapFocus(extraParams.trapFocus);
      stores.contextMenuStore.setContextMenuStyle(extraParams.contextMenuStyle ? extraParams.contextMenuStyle : {});
      stores.contextMenuStore.setContextMenuItemStyle(
        extraParams.contextMenuItemStyle ? extraParams.contextMenuItemStyle : {}
      );

      const signalDispatcher = this.getSignalDispatcherByType(type);
      signalDispatcher.trigger(type, eventObj, stores, extraParams).then(menuItems => {
        const useVerb = !(extraParams && extraParams.menutItemsUsingObj);
        if (getShouldUseModernViewer(stores) && useVerb) {
          if (
            type === contextMenuTriggerType.CALENDAR ||
            type === contextMenuTriggerType.COLOR_PICKER ||
            type === contextMenuTriggerType.SCALE_ANNOT
          ) {
            if (
              !isUndefined(menuItems[0]) &&
              "component" in menuItems[0] &&
              "width" in menuItems[0] &&
              "height" in menuItems[0]
            ) {
              stores.contextMenuStore.addMenuItems(menuItems);
            }
          } else {
            menuItems = flatten(menuItems);
            sortAndGetMenuItems(menuItems, stores).then(sortedMenuItems => {
              stores.contextMenuStore.addMenuItems(sortedMenuItems);
            });
          }
        } else {
          menuItems = flatten(menuItems);
          if (
            type !== contextMenuTriggerType.RIGHT_CLICK &&
            type !== contextMenuTriggerType.HUD_OVERFLOW_BUTTON_CLICK &&
            !extraParams.isContextMenu
          ) {
            const filteredMenuItems = this.filterAndSortContextualToolItem(menuItems);
            stores.contextMenuStore.addMenuItems(filteredMenuItems);
          } else {
            const filteredMenuItems = this.filterAndSortMenuItems(menuItems);
            stores.contextMenuStore.addMenuItems(filteredMenuItems);
          }
        }
      });
    }
  }

  unsubscribe(type = contextMenuTriggerType.RIGHT_CLICK) {
    delete this.signalDispatcherMap[type];
  }

  _shoudAddEditMenuItem(menuItem) {
    const { props } = menuItem.component;
    return !(props && props.type === "edit" && pathExistsInPathName(constants.commentModePathName));
  }

  filterAndSortContextualToolItem(menuItems) {
    let validMenuItems = [];

    forEach(menuItems, menuItem => {
      if (!isUndefined(menuItem) && "component" in menuItem && "width" in menuItem && "height" in menuItem) {
        if (!("position" in menuItem)) {
          menuItem.position = 1000; // If no position level is defined for the item, push it to the back
        }
        if (this._shoudAddEditMenuItem(menuItem)) validMenuItems.push(menuItem);
      }
    });
    validMenuItems.sort((a, b) => a.position - b.position); // sort menuitems corresponding to their position levels
    return validMenuItems;
  }

  filterAndSortMenuItems(menuItems) {
    let validMenuItems = [];

    forEach(menuItems, menuItem => {
      if (
        (!isUndefined(menuItem) &&
          "title" in menuItem &&
          "eventName" in menuItem &&
          "eventHandler" in menuItem &&
          !isUndefined(menuItem.title) &&
          !isUndefined(menuItem.eventName) &&
          isFunction(menuItem.eventHandler)) ||
        (!isUndefined(menuItem) && "title" in menuItem && menuItem.title === "separator")
      ) {
        validMenuItems.push(menuItem);
      }
      if (!("position" in menuItem)) {
        menuItem.position = 1000; // If no position level is defined for the item, push it to the back
      }
    });

    validMenuItems.sort((a, b) => a.position - b.position); // sort menuitems corresponding to their position levels
    return validMenuItems;
  }

  clearMenuItems(stores) {
    if (isContextMenuVisible(stores)) {
      stores.contextMenuStore.clearMenuItems();
    }
  }

  handleMouseDown(stores) {
    this.clearMenuItems(stores);
  }
}

export const contextMenuManager = new ContextMenuManager();
